import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Document, Page, View, pdf } from '@react-pdf/renderer';

import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import Barcode from 'pages/Settings/Print/components/common/Barcode';
import usePrintUtils from 'pages/Settings/Print/hooks/usePrintUtils';

import AppLogger from 'utils/AppLogger';

const BarcodePageM212 = ({ barcodes }) => (
	<Document>
		<Page style={{ margin: 0 }} size={{ width: '100mm', height: '50mm' }}>
			<View
				style={{
					width: '100mm',
					height: '50mm',
					display: 'flex',
					justifyContent: 'center'
				}}>
				<Barcode value={barcodes.values} hideText={true} options={barcodes} />
			</View>
		</Page>
	</Document>
);

const BarcodePageM42 = ({ barcodes }) => (
	<Document>
		<Page style={{ margin: 0 }} size={{ width: '100mm', height: '50mm' }}>
			<View
				style={{
					width: '100mm',
					height: '50mm',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					alignItems: 'center'
				}}>
				<Barcode value={barcodes.values[0]} hideText={true} options={barcodes} />
				<Barcode value={barcodes.values[1]} hideText={true} options={barcodes} />
			</View>
		</Page>
	</Document>
);

const BarcodeGeneratorModal = NiceModal.create(() => {
	const [barcodes, setBarcodes] = useState('');
	const [loading, setLoading] = useState(false);
	const [pageType, setPageType] = useState('(100mmx50mmx1-Row)');
	const [barcodeType, setBarcodeType] = useState('CODE128');
	const [barcodeError, setBarcodeError] = useState(null);
	const modal = useModal();
	const { mergePDF, pdfFile } = usePrintUtils();
	const { mobile = false } = useSelector((state) => state?.layout);

	const barcodeCode = useCallback(() => {
		switch (barcodeType) {
			case 'CODE128':
				return 'CODE128';
			case 'CODE39':
				return 'CODE39';
			case 'EAN13(12 DIGIT)':
				return 'EAN13';
			case 'UPC(11 DIGIT)':
				return 'UPC';
		}
	}, [barcodeType]);

	const generate = async () => {
		if (!barcodes) {
			setBarcodeError('alert.requiredField');
			return;
		}
		setLoading(true);
		const marginTop = pageType === '(100mmx50mmx1-Row)' ? 35 : 45;
		try {
			const pdfUrl = await mergePDF(async (merger) => {
				if (pageType === '(100mmx50mmx1-Row)') {
					// foreach barcode create a blog and add to merger in async foreach
					for (const barcode of barcodes.split('\n')) {
						const blob = await pdf(
							<BarcodePageM212
								barcodes={{
									values: barcode,
									format: barcodeCode(),
									fontSize: 21,
									marginTop,
									width: barcodeType === 'CODE39' ? 1 : 2,
									fontOptions: 'bold'
								}}
							/>
						).toBlob();
						await merger.add(blob);
					}
				} else {
					// foreach two barcode create a blog and add to merger in async foreach
					// for odd numbers last barcode will be single with BarcodePageM212
					for (let i = 0; i < barcodes.split('\n').length; i += 2) {
						if (i === barcodes.split('\n').length - 1) {
							const blob = await pdf(
								<BarcodePageM212
									barcodes={{
										values: barcodes.split('\n')[i],
										format: barcodeCode(),
										fontSize: 21,
										marginTop,
										width: barcodeType === 'CODE39' ? 1 : 2,
										fontOptions: 'bold'
									}}
								/>
							).toBlob();
							await merger.add(blob);
						} else {
							const blob = await pdf(
								<BarcodePageM42
									barcodes={{
										values: [barcodes.split('\n')[i], barcodes.split('\n')[i + 1]],
										format: barcodeCode(),
										fontSize: 21,
										marginTop,
										width: barcodeType === 'CODE39' ? 1 : 2,
										fontOptions: 'bold'
									}}
								/>
							).toBlob();
							await merger.add(blob);
						}
					}
				}
				const mergedPDF = await merger.saveAsBlob();
				return mergedPDF;
			});
			if (mobile) {
				const link = document.createElement('a');
				link.href = pdfUrl;
				link.target = '_blank';
				link.setAttribute('download', `${barcodes?.split('\n').join(',')}_${barcodeType}.pdf`);

				document.body.appendChild(link);
				link.click();
				link.parentNode.removeChild(link);
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal fullwidth maxWidth="medium" className="modal-barcodeGenerator">
			<ModalTitle>
				<Typography variant="p1">barcodeGenerator</Typography>
			</ModalTitle>
			<ModalContent>
				<Form>
					<FormItem label="barcodes" toolTip="barcodeTextAreaTooltip">
						<TextArea
							value={barcodes}
							setValue={(val) => {
								setBarcodes(val);
								barcodeError && setBarcodeError(null);
							}}
							maxlength={5000}
							errorMessage={barcodeError}
						/>
					</FormItem>
					<FormItem label="barcodeInvoice" colSpan={{ md: 6 }}>
						<EnumSelect
							value={pageType}
							setValue={setPageType}
							data={['(100mmx50mmx1-Row)', '(50mmx25mmx2-Row)']}
							searchable={false}
							valueRemovable={false}
						/>
					</FormItem>
					<FormItem label="barcodeType" colSpan={{ md: 6 }}>
						<EnumSelect
							value={barcodeType}
							setValue={setBarcodeType}
							data={['CODE128', 'CODE39', 'EAN13(12 DIGIT)', 'UPC(11 DIGIT)']}
							searchable={false}
							valueRemovable={false}
						/>
					</FormItem>
					{pdfFile && !mobile && (
						<div
							style={{ width: '100%', minHeight: '500px' }}
							dangerouslySetInnerHTML={{
								__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
							}}
						/>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={() => modal.remove()} />

				<Button label="generate" onClick={() => generate()} loading={loading} />
			</ModalActions>
		</Modal>
	);
});

export default BarcodeGeneratorModal;
